import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { normLang } from "@/i18n";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, X, Search as SearchIcon, AlertTriangle } from "lucide-react";

const TXT = {
  fr: {
    pageTitle: "Centre d'alertes risque",
    pageSub: "Signaux HIGH/CRITICAL à examiner — aucune action automatique. L'administrateur décide.",
    refresh: "Actualiser",
    new: "Nouvelles", reviewing: "En cours", watch: "À surveiller", resolved: "Résolues",
    allLevels: "Tous niveaux", allStates: "Tous états",
    date: "Date", level: "Niveau", signal: "Signal", member: "Membre", withdrawal: "Retrait",
    amount: "Montant", state: "État", action: "Action",
    examine: "Examiner", noAlerts: "Aucune alerte.",
    detailTitle: "Détail de l'alerte",
    description: "Description", memberCode: "Member Code", memberRisk: "Risk state membre",
    amountMethod: "Montant / méthode", wdStatus: "Statut retrait", createdOn: "Créée le",
    signalsProofs: "Signaux / preuves", handlingHistory: "Historique de traitement",
    notePh: "Commentaire (obligatoire pour résoudre)",
    markReviewed: "Marquer examinée", toWatch: "À surveiller", resolve: "Résoudre", reopen: "Rouvrir",
    noteRequired: "Un commentaire est requis", alertUpdated: "Alerte mise à jour",
  },
  en: {
    pageTitle: "Risk alert center",
    pageSub: "HIGH/CRITICAL signals to review — no automatic action. The administrator decides.",
    refresh: "Refresh",
    new: "New", reviewing: "In progress", watch: "To watch", resolved: "Resolved",
    allLevels: "All levels", allStates: "All states",
    date: "Date", level: "Level", signal: "Signal", member: "Member", withdrawal: "Withdrawal",
    amount: "Amount", state: "State", action: "Action",
    examine: "Review", noAlerts: "No alert.",
    detailTitle: "Alert detail",
    description: "Description", memberCode: "Member Code", memberRisk: "Member risk state",
    amountMethod: "Amount / method", wdStatus: "Withdrawal status", createdOn: "Created on",
    signalsProofs: "Signals / evidence", handlingHistory: "Handling history",
    notePh: "Comment (required to resolve)",
    markReviewed: "Mark reviewed", toWatch: "To watch", resolve: "Resolve", reopen: "Reopen",
    noteRequired: "A comment is required", alertUpdated: "Alert updated",
  },
};
const useT = () => { const { i18n } = useTranslation(); return TXT[normLang(i18n.language)] || TXT.fr; };

const LEVEL_STYLE = {
  CRITICAL: "bg-rose-500/15 text-rose-500 border-rose-500/40",
  HIGH: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  MEDIUM: "bg-sky-500/10 text-sky-500 border-sky-500/30",
  LOW: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};
const STATE_STYLE = {
  NEW: "bg-rose-500/10 text-rose-500 border-rose-500/30",
  REVIEWING: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  WATCH: "bg-sky-500/10 text-sky-500 border-sky-500/30",
  RESOLVED: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
};

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-display text-lg font-bold ${tone || ""}`}>{value}</div>
    </div>
  );
}

function DetailModal({ alertId, onClose, onActed }) {
  const L = useT();
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/admin/risk/alerts/${alertId}`).then((r) => setA(r.data)).catch((e) => toast.error(apiError(e?.response?.data?.detail))); }, [alertId]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, needNote) => {
    if (needNote && !note.trim()) { toast.error(L.noteRequired); return; }
    setBusy(true);
    try { await api.post(`/admin/risk/alerts/${alertId}/action`, { action, note }); toast.success(L.alertUpdated); onActed?.(); load(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  if (!a) return null;
  const w = a.withdrawal;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="my-6 w-full max-w-lg rounded-2xl border border-border bg-card p-5" data-testid="risk-detail-modal">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold"><ShieldAlert className="h-5 w-5 text-amber-500" /> {L.detailTitle}</h3>
          <button onClick={onClose} data-testid="risk-detail-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${LEVEL_STYLE[a.level] || ""}`}>{a.level}</span>
          <span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${STATE_STYLE[a.handling_state] || ""}`}>{a.handling_state}</span>
          <span className="text-xs text-muted-foreground">{a.signal_label}</span>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">{L.description}</span><span className="max-w-[60%] text-right">{a.description}</span></div>
          {a.member_code && <div className="flex justify-between"><span className="text-muted-foreground">{L.memberCode}</span><span className="font-mono">{a.member_code}</span></div>}
          {a.member_risk_state && <div className="flex justify-between"><span className="text-muted-foreground">{L.memberRisk}</span><span>{a.member_risk_state}</span></div>}
          {a.withdrawal_id && <div className="flex justify-between"><span className="text-muted-foreground">{L.withdrawal}</span><span className="font-mono">{w?.reference || a.withdrawal_id}</span></div>}
          {w && <div className="flex justify-between"><span className="text-muted-foreground">{L.amountMethod}</span><span>{money(w.requested_amount)} · {w.method === "usdt" ? "USDT" : "Mobile Money"} {w.mm_country || ""}</span></div>}
          {w && <div className="flex justify-between"><span className="text-muted-foreground">{L.wdStatus}</span><span>{w.status}</span></div>}
          <div className="flex justify-between"><span className="text-muted-foreground">{L.createdOn}</span><span>{(a.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
        </div>

        {(a.signals?.length || Object.keys(a.evidence || {}).length) ? (
          <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3 text-xs">
            <div className="mb-1 font-semibold text-muted-foreground">{L.signalsProofs}</div>
            {(a.signals || []).map((s, i) => <div key={i}>• {s}</div>)}
            {Object.entries(a.evidence || {}).map(([k, v]) => <div key={k}>• {k}: {String(v)}</div>)}
          </div>
        ) : null}

        <div className="mt-3 rounded-lg bg-amber-500/10 p-2 text-[11px] text-amber-600" data-testid="risk-disclaimer">{a.disclaimer}</div>

        {a.history?.length ? (
          <div className="mt-3 max-h-28 overflow-auto rounded-lg border border-border p-2 text-[11px]">
            <div className="mb-1 font-semibold text-muted-foreground">{L.handlingHistory}</div>
            {a.history.map((hx, i) => (
              <div key={i} className="flex justify-between"><span>{hx.after?.handling_state} · {hx.evidence?.note || "—"}</span><span className="text-muted-foreground">{hx.actor} · {(hx.created_at || "").slice(0, 16).replace("T", " ")}</span></div>
            ))}
          </div>
        ) : null}

        <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="risk-action-note" placeholder={L.notePh}
          className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" rows={2} />
        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={() => act("review", false)} disabled={busy} data-testid="risk-action-review" className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white">{L.markReviewed}</button>
          <button onClick={() => act("watch", false)} disabled={busy} data-testid="risk-action-watch" className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white">{L.toWatch}</button>
          <button onClick={() => act("resolve", true)} disabled={busy} data-testid="risk-action-resolve" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">{L.resolve}</button>
          {a.handling_state === "RESOLVED" && <button onClick={() => act("reopen", false)} disabled={busy} data-testid="risk-action-reopen" className="rounded-lg border border-border px-3 py-1.5 text-xs">{L.reopen}</button>}
        </div>
      </div>
    </div>
  );
}

export default function AdminRiskCenter() {
  const L = useT();
  const [data, setData] = useState({ items: [], count: 0 });
  const [summary, setSummary] = useState({});
  const [level, setLevel] = useState("");
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/risk/alerts", { params: { level, state, q } }),
        api.get("/admin/risk/summary"),
      ]);
      setData(a.data); setSummary(s.data);
    } catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }, [level, state, q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="admin-risk-center">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold"><ShieldAlert className="h-5 w-5 text-amber-500" /> {L.pageTitle}</h1>
          <p className="text-sm text-muted-foreground">{L.pageSub}</p>
        </div>
        <button onClick={load} data-testid="risk-refresh" className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-secondary"><RefreshCw className="h-4 w-4" /> {L.refresh}</button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="CRITICAL" value={summary.critical ?? 0} tone="text-rose-500" />
        <Stat label="HIGH" value={summary.high ?? 0} tone="text-amber-500" />
        <Stat label={L.new} value={summary.new ?? 0} />
        <Stat label={L.reviewing} value={summary.reviewing ?? 0} />
        <Stat label={L.watch} value={summary.watch ?? 0} />
        <Stat label={L.resolved} value={summary.resolved ?? 0} tone="text-emerald-500" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={level} onChange={(e) => setLevel(e.target.value)} data-testid="risk-filter-level" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">HIGH + CRITICAL</option>
          <option value="CRITICAL">CRITICAL</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
          <option value="ALL">{L.allLevels}</option>
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)} data-testid="risk-filter-state" className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm">
          <option value="">{L.allStates}</option>
          {["NEW", "REVIEWING", "WATCH", "RESOLVED"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2">
          <SearchIcon className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="risk-search" placeholder="member_code / withdrawal_id" className="bg-transparent py-1.5 text-sm outline-none" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs text-muted-foreground"><tr>
            <th className="px-2 py-2 text-left">{L.date}</th><th className="px-2 py-2 text-left">{L.level}</th>
            <th className="px-2 py-2 text-left">{L.signal}</th><th className="px-2 py-2 text-left">{L.member}</th>
            <th className="px-2 py-2 text-left">{L.withdrawal}</th><th className="px-2 py-2 text-right">{L.amount}</th>
            <th className="px-2 py-2 text-left">{L.state}</th><th className="px-2 py-2 text-right">{L.action}</th>
          </tr></thead>
          <tbody>
            {data.items.map((a) => (
              <tr key={a.alert_id} className="border-t border-border" data-testid={`risk-row-${a.alert_id}`}>
                <td className="px-2 py-2 text-xs text-muted-foreground">{(a.created_at || "").slice(0, 10)}</td>
                <td className="px-2 py-2"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-bold ${LEVEL_STYLE[a.level] || ""}`}>{a.level}</span></td>
                <td className="px-2 py-2 text-xs">{a.signal_label}</td>
                <td className="px-2 py-2 font-mono text-xs">{a.member_code || "—"}</td>
                <td className="px-2 py-2 text-xs">{a.withdrawal_id ? (a.method === "usdt" ? "USDT" : `MM ${a.mm_country || ""}`) : "—"}</td>
                <td className="px-2 py-2 text-right">{a.amount != null ? money(a.amount) : "—"}</td>
                <td className="px-2 py-2"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${STATE_STYLE[a.handling_state] || ""}`}>{a.handling_state}</span></td>
                <td className="px-2 py-2 text-right">
                  <button onClick={() => setOpenId(a.alert_id)} data-testid={`risk-open-${a.alert_id}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-secondary"><AlertTriangle className="h-3.5 w-3.5" /> {L.examine}</button>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={8} className="px-2 py-6 text-center text-muted-foreground">{L.noAlerts}</td></tr>}
          </tbody>
        </table>
      </div>

      {openId && <DetailModal alertId={openId} onClose={() => setOpenId(null)} onActed={load} />}
    </div>
  );
}
